import React, { useMemo } from 'react';

import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faCalendarDay,
  faCalendarWeek,
  faClock,
  faList,
  faSquare,
  faSquareCheck,
} from '@fortawesome/pro-regular-svg-icons';
import { Tab, Tabs, TabsList } from '@buildoutinc/blueprint-react/ui/Tabs';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import { ISO_DATE_FORMAT } from 'helpers/formatters/date_time_formats';
import { useTasks } from '../context/tasks_context';

import type { Filters } from '../filters/types';

type StatusKey = 'all' | 'incomplete' | 'overdue' | 'completed';
type DueDateKey = 'today' | 'week' | 'month';

const SHOW_ALL_VALUE = 'show-all';

interface StatusOption {
  key: StatusKey;
  label: string;
  icon: IconDefinition;
}

interface DueDateOption {
  key: DueDateKey;
  label: string;
  icon: IconDefinition;
}

const STATUS_OPTIONS: StatusOption[] = [
  { key: 'incomplete', label: 'Incomplete', icon: faSquare },
  { key: 'overdue', label: 'Overdue', icon: faClock },
  { key: 'completed', label: 'Completed', icon: faSquareCheck },
];

const DUE_DATE_OPTIONS: DueDateOption[] = [
  { key: 'today', label: 'Due Today', icon: faCalendarDay },
  { key: 'week', label: 'Upcoming Week', icon: faCalendarWeek },
  { key: 'month', label: 'Upcoming 30 Days', icon: faCalendar },
];

function statusKeyFromFilters({ completed, open, overdue }: Filters): StatusKey {
  if (completed && !open && !overdue) return 'completed';
  if (overdue && !completed && !open) return 'overdue';
  if (open && overdue && !completed) return 'incomplete';
  return 'all';
}

function dueDateKeyFromFilters({ fromDate, toDate }: Filters): DueDateKey | null {
  if (!fromDate || !toDate) return null;
  const today = moment().format(ISO_DATE_FORMAT);
  if (fromDate !== today) return null;
  if (toDate === today) return 'today';
  if (toDate === moment().add(7, 'days').format(ISO_DATE_FORMAT)) return 'week';
  if (toDate === moment().add(30, 'days').format(ISO_DATE_FORMAT)) return 'month';
  return null;
}

function statusChanges(key: StatusKey): Partial<Filters> {
  switch (key) {
  case 'incomplete':
    return { completed: false, open: true, overdue: true };
  case 'overdue':
    return { completed: false, open: false, overdue: true };
  case 'completed':
    return { completed: true, open: false, overdue: false };
  case 'all':
  default:
    return { completed: false, open: false, overdue: false };
  }
}

function dueDateChanges(key: DueDateKey): Partial<Filters> {
  const today = moment().format(ISO_DATE_FORMAT);
  switch (key) {
  case 'today':
    return { fromDate: today, toDate: today };
  case 'week':
    return { fromDate: today, toDate: moment().add(7, 'days').format(ISO_DATE_FORMAT) };
  case 'month':
    return { fromDate: today, toDate: moment().add(30, 'days').format(ISO_DATE_FORMAT) };
  }
}

export function TasksQuickFilters() {
  const { filters, changeFilters } = useTasks();

  const activeStatus = useMemo(() => statusKeyFromFilters(filters), [filters]);
  const activeDueDate = useMemo(() => dueDateKeyFromFilters(filters), [filters]);
  const showAllActive = activeStatus === 'all' && activeDueDate === null;

  function selectStatus(key: StatusKey) {
    const next: StatusKey = activeStatus === key ? 'all' : key;
    const changes: Partial<Filters> = statusChanges(next);
    // Overdue (deadline < today) can never intersect with a Due Date preset (deadline >= today),
    // so clear the date filter when Overdue is picked.
    if (next === 'overdue') {
      changes.fromDate = null;
      changes.toDate = null;
    }
    changeFilters(changes);
  }

  function selectDueDate(key: DueDateKey) {
    if (activeDueDate === key) {
      changeFilters({ fromDate: null, toDate: null });
    } else {
      changeFilters(dueDateChanges(key));
    }
  }

  function showAll() {
    if (showAllActive) return;
    changeFilters({ ...statusChanges('all'), fromDate: null, toDate: null });
  }

  return (
    <div className="d-flex flex-column gap-4" style={{ minWidth: '220px' }}>
      <Tabs orientation="vertical" value={showAllActive ? SHOW_ALL_VALUE : null}>
        <TabsList orientation="vertical" variant="pills">
          <Tab
            icon={<FontAwesomeIcon icon={faList} />}
            value={SHOW_ALL_VALUE}
            onClick={showAll}
          >
            Show All
          </Tab>
        </TabsList>
      </Tabs>

      <div className="d-flex flex-column gap-2">
        <div className="fs-large fw-semibold lh-large">Status</div>
        <Tabs orientation="vertical" value={activeStatus === 'all' ? null : activeStatus}>
          <TabsList orientation="vertical" variant="pills">
            {STATUS_OPTIONS.map(option => (
              <Tab
                icon={<FontAwesomeIcon icon={option.icon} />}
                key={option.key}
                value={option.key}
                onClick={() => selectStatus(option.key)}
              >
                {option.label}
              </Tab>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="d-flex flex-column gap-2">
        <div className="fs-large fw-semibold lh-large">Due Date</div>
        <Tabs orientation="vertical" value={activeDueDate}>
          <TabsList orientation="vertical" variant="pills">
            {DUE_DATE_OPTIONS.map(option => (
              <Tab
                icon={<FontAwesomeIcon icon={option.icon} />}
                key={option.key}
                value={option.key}
                onClick={() => selectDueDate(option.key)}
              >
                {option.label}
              </Tab>
            ))}
          </TabsList>
        </Tabs>
      </div>
    </div>
  );
}
